import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HiPlus, HiTrash, HiExclamationCircle } from 'react-icons/hi';
import {
  CreatePriceListRequest,
  UpdatePriceListRequest,
  PriceListFormTab,
  PriceList,
  PriceListStatus,
  CustomerSelectionItem,
  ProductSelectionItem,
  CreatePriceListLineRequest,
} from '@/types/pricing';
import { CustomerGroup } from '@/types/customer';
import { Button } from '@/components/common';
import { pricingApi } from '@/api/endpoints/pricing';
import { FormattedNumberInput } from '@/utils/number-format';

interface PriceListFormProps {
  priceList?: PriceList;
  onSubmit: (data: CreatePriceListRequest | UpdatePriceListRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
  }).format(amount);
};

export const PriceListForm: React.FC<PriceListFormProps> = ({
  priceList,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<PriceListFormTab>('info');
  
  // Form state
  const [formData, setFormData] = useState<CreatePriceListRequest>({
    name: '',
    description: '',
    status: 'draft',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    isDefault: false,
    priority: 10,
    applicableCustomers: [],
    applicableCustomerGroups: [],
    priceListLines: [],
  });

  // Selection data
  const [availableCustomers, setAvailableCustomers] = useState<CustomerSelectionItem[]>([]);
  const [availableGroups, setAvailableGroups] = useState<CustomerGroup[]>([]);
  const [availableProducts, setAvailableProducts] = useState<ProductSelectionItem[]>([]);
  
  // UI state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [customerSearch, setCustomerSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');

  // Initialize form with existing price list data
  useEffect(() => {
    if (priceList) {
      setFormData({
        name: priceList.name,
        description: priceList.description || '',
        status: priceList.status,
        startDate: priceList.startDate.split('T')[0],
        endDate: priceList.endDate ? priceList.endDate.split('T')[0] : '',
        isDefault: priceList.isDefault,
        priority: priceList.priority,
        applicableCustomers: priceList.applicableCustomers,
        applicableCustomerGroups: priceList.applicableCustomerGroups,
        pricingFormula: priceList.pricingFormula,
        basePriceListId: priceList.basePriceListId,
        priceListLines: priceList.priceListLines.map(line => ({
          productId: line.productId,
          specialPrice: line.specialPrice,
          discountPercentage: line.discountPercentage,
          minQuantity: line.minQuantity,
          maxQuantity: line.maxQuantity,
          notes: line.notes,
          isActive: line.isActive,
        })),
      });
    }
  }, [priceList]);

  // Load data for dropdowns
  useEffect(() => {
    const loadData = async () => {
      try {
        const [customers, groups, products] = await Promise.all([
          pricingApi.getCustomersForPricing(customerSearch),
          pricingApi.getCustomerGroupsForPricing(),
          pricingApi.getProductsForSelection(productSearch),
        ]);
        
        setAvailableCustomers(customers);
        setAvailableGroups(groups.data);
        setAvailableProducts(products);
      } catch (error) {
        console.error('Error loading form data:', error);
      }
    };
    
    loadData();
  }, [customerSearch, productSearch]);

  const handleInputChange = (field: keyof CreatePriceListRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCustomerToggle = (customerId: string) => {
    const isSelected = formData.applicableCustomers.includes(customerId);
    const newCustomers = isSelected
      ? formData.applicableCustomers.filter(id => id !== customerId)
      : [...formData.applicableCustomers, customerId];
    
    handleInputChange('applicableCustomers', newCustomers);
  };

  const handleGroupToggle = (groupId: string) => {
    const isSelected = formData.applicableCustomerGroups.includes(groupId);
    const newGroups = isSelected
      ? formData.applicableCustomerGroups.filter(id => id !== groupId)
      : [...formData.applicableCustomerGroups, groupId];
    
    handleInputChange('applicableCustomerGroups', newGroups);
  };

  const handleProductAdd = (product: ProductSelectionItem) => {
    const isAlreadyAdded = formData.priceListLines.some(line => line.productId === product.id);
    if (isAlreadyAdded) return;

    const newLine: CreatePriceListLineRequest = {
      productId: product.id,
      specialPrice: product.basePrice,
      discountPercentage: 0,
      isActive: true,
    };

    handleInputChange('priceListLines', [...formData.priceListLines, newLine]);
  };

  const handleProductRemove = (productId: string) => {
    const newLines = formData.priceListLines.filter(line => line.productId !== productId);
    handleInputChange('priceListLines', newLines);
  };

  const handleLineChange = (index: number, field: keyof CreatePriceListLineRequest, value: any) => {
    const newLines = [...formData.priceListLines];
    newLines[index] = { ...newLines[index], [field]: value };
    
    // If changing special price, clear discount percentage and vice versa
    if (field === 'specialPrice' && value) {
      newLines[index].discountPercentage = undefined;
    } else if (field === 'discountPercentage' && value) {
      newLines[index].specialPrice = undefined;
    }
    
    handleInputChange('priceListLines', newLines);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('pricing.validation.nameRequired');
    }

    if (!formData.startDate) {
      newErrors.startDate = t('pricing.validation.startDateRequired');
    }

    if (formData.endDate && formData.startDate && new Date(formData.endDate) <= new Date(formData.startDate)) {
      newErrors.endDate = t('pricing.validation.invalidDateRange');
    }

    if (formData.applicableCustomers.length === 0 && formData.applicableCustomerGroups.length === 0 && !formData.isDefault) {
      newErrors.scope = t('pricing.validation.noCustomersSelected');
    }

    if (formData.priceListLines.length === 0) {
      newErrors.products = t('pricing.validation.noProductsSelected');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const submitData = priceList
        ? { ...formData, id: priceList.id } as UpdatePriceListRequest
        : formData;
      
      await onSubmit(submitData);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const tabs = [
    { key: 'info' as const, label: t('pricing.tabs.info') },
    { key: 'scope' as const, label: t('pricing.tabs.scope') },
    { key: 'setup' as const, label: t('pricing.tabs.setup') },
  ];

  const getTabClasses = (tabKey: PriceListFormTab) => {
    const baseClasses = 'mobile-tab-button border-b-2 cursor-pointer';
    const hasError = (tabKey === 'info' && (errors.name || errors.startDate || errors.endDate)) ||
                    (tabKey === 'scope' && errors.scope) ||
                    (tabKey === 'setup' && errors.products);
    
    if (activeTab === tabKey) {
      return `${baseClasses} text-blue-700 border-blue-500 bg-blue-50 ${hasError ? 'bg-red-50 text-red-700 border-red-500' : ''}`;
    }
    
    return `${baseClasses} text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300 ${hasError ? 'text-red-500' : ''}`;
  };

  const getSelectedCustomerNames = () => {
    return formData.applicableCustomers.map(id => {
      const customer = availableCustomers.find(c => c.id === id);
      return customer ? customer.name : id;
    }).join(', ');
  };

  const getSelectedGroupNames = () => {
    return formData.applicableCustomerGroups.map(id => {
      const group = availableGroups.find(g => g.id === id);
      return group ? group.name : id;
    }).join(', ');
  };

  const getProductName = (productId: string) => {
    const product = availableProducts.find(p => p.id === productId);
    return product ? `${product.code} - ${product.name}` : productId;
  };

  const getProductBasePrice = (productId: string) => {
    const product = availableProducts.find(p => p.id === productId);
    return product ? product.basePrice : 0;
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="mobile-tab-scroll">
        <nav className="mobile-tab-list" aria-label="Price List Form Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={getTabClasses(tab.key)}
            >
              {tab.label}
              {((tab.key === 'info' && (errors.name || errors.startDate || errors.endDate)) ||
                (tab.key === 'scope' && errors.scope) ||
                (tab.key === 'setup' && errors.products)) && (
                <HiExclamationCircle className="ml-1 h-4 w-4 text-red-500" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* Tab 1: General Info */}
        {activeTab === 'info' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('pricing.fields.name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder={t('pricing.fields.name')}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('pricing.fields.status')}
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value as PriceListStatus)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="draft">{t('pricing.status.draft')}</option>
                  <option value="active">{t('pricing.status.active')}</option>
                  <option value="inactive">{t('pricing.status.inactive')}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('pricing.fields.description')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('pricing.fields.description')}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('pricing.fields.startDate')} *
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.startDate ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.startDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('pricing.fields.endDate')}
                  <span className="text-xs text-gray-500 ml-1">({t('pricing.tooltips.unlimitedValidity')})</span>
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.endDate ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.endDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('pricing.fields.priority')}
                  <span className="text-xs text-gray-500 ml-1">({t('pricing.tooltips.priority')})</span>
                </label>
                <FormattedNumberInput
                  value={formData.priority || 10}
                  onChange={(value) => handleInputChange('priority', Math.max(1, Math.min(999, value)) || 10)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="isDefault" className="ml-2 text-sm text-gray-700">
                  {t('pricing.fields.isDefault')}
                  <span className="text-xs text-gray-500 ml-1">({t('pricing.tooltips.defaultPriceList')})</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Applicable Scope */}
        {activeTab === 'scope' && (
          <div className="space-y-6">
            {errors.scope && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600">{errors.scope}</p>
              </div>
            )}

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('pricing.customerSelection.title')}
              </h3>
              
              <div className="mb-4">
                <input
                  type="text"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder={t('pricing.customerSelection.searchPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                {availableCustomers.map((customer) => (
                  <label
                    key={customer.id}
                    className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={formData.applicableCustomers.includes(customer.id)}
                      onChange={() => handleCustomerToggle(customer.id)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {customer.code} - {customer.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {customer.type === 'individual' ? t('pricing.customerSelection.individual') : t('pricing.customerSelection.company')}
                            {customer.groupName && ` • ${customer.groupName}`}
                          </p>
                        </div>
                        {customer.hasSpecialPricing && (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                            {t('pricing.customerSelection.hasSpecialPricing')}
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {formData.applicableCustomers.length > 0 && (
                <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    {t('pricing.customerSelection.selectedCount', { count: formData.applicableCustomers.length })}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">{getSelectedCustomerNames()}</p>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('pricing.customerSelection.group')}
              </h3>
              
              <div className="space-y-2">
                {availableGroups.map((group) => (
                  <label
                    key={group.id}
                    className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.applicableCustomerGroups.includes(group.id)}
                      onChange={() => handleGroupToggle(group.id)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{group.name}</p>
                          {group.description && (
                            <p className="text-xs text-gray-500">{group.description}</p>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">
                          {group.customerCount} {t('common.customers')}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {formData.applicableCustomerGroups.length > 0 && (
                <div className="mt-2 p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-700">
                    {t('pricing.customerSelection.selectedCount', { count: formData.applicableCustomerGroups.length })} groups
                  </p>
                  <p className="text-xs text-green-600 mt-1">{getSelectedGroupNames()}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 3: Price Setup */}
        {activeTab === 'setup' && (
          <div className="space-y-6">
            {errors.products && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600">{errors.products}</p>
              </div>
            )}

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('pricing.productSelection.title')}
              </h3>
              
              <div className="mb-4">
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder={t('pricing.productSelection.searchPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Available Products */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    {t('common.availableProducts')}
                  </h4>
                  <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                    {availableProducts
                      .filter(product => !formData.priceListLines.some(line => line.productId === product.id))
                      .map((product) => (
                        <div
                          key={product.id}
                          className="flex items-center justify-between p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {product.code} - {product.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {product.categoryName} • {formatCurrency(product.basePrice)}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleProductAdd(product)}
                          >
                            <HiPlus className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Selected Products with Pricing */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    {t('pricing.productSelection.selectedCount', { count: formData.priceListLines.length })}
                  </h4>
                  <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                    {formData.priceListLines.map((line, index) => (
                      <div key={line.productId} className="p-3 border-b border-gray-100 last:border-b-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {getProductName(line.productId)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {t('pricing.fields.basePrice')}: {formatCurrency(getProductBasePrice(line.productId))}
                            </p>
                          </div>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleProductRemove(line.productId)}
                          >
                            <HiTrash className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">
                              {t('pricing.fields.specialPrice')}
                            </label>
                            <FormattedNumberInput
                              value={line.specialPrice || 0}
                              onChange={(value) => handleLineChange(index, 'specialPrice', value || undefined)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">
                              {t('pricing.fields.discountPercentage')}
                            </label>
                            <FormattedNumberInput
                              value={line.discountPercentage || 0}
                              onChange={(value) => handleLineChange(index, 'discountPercentage', Math.max(0, Math.min(100, value)) || undefined)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                              placeholder="0"
                              decimalPlaces={1}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
        <Button
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? t('common.saving') : priceList ? t('pricing.actions.save') : t('pricing.actions.create')}
        </Button>
      </div>
    </div>
  );
};
